// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a release's auxiliary class map (IMERG's rain/snow "phase"),
 * turned into what the globes and the readout need (owner, 2026-10-04:
 * "in 2D/3D they are read properly but not here"). Ported from AYNI 2D
 * (`luma-auxiliary.ts`, `luma-class-lut.ts`), fitted to this site's
 * shaders.
 *
 * The frame holds the values in `dataRegion` and, in another rectangle,
 * a classified map: each of its pixels says which class the value at the
 * same place belongs to. A class with its own palette (snow) is drawn
 * with it; the others (rain) with the default palette.
 *
 * What a shader gets, the same on the browser globe and in VR:
 *
 *  - **the palette table**, 256 wide and one row per palette: row 0 is the
 *    default palette as it is drawn now (the display transform included),
 *    then one row per class that has a palette, in class order. A class row
 *    takes its colour from the class's palette at the same palette position
 *    and its alpha from row 0: opacity says how much was measured, not
 *    which class it fell in (AYNI 2D does the same).
 *  - **the class map**, 256×1, kept apart because a three.js colour texture
 *    is decoded from sRGB on sampling, which would garble numbers: the red
 *    byte of entry `code` is the palette row for an auxiliary pixel of that
 *    code (0 for the default palette).
 *  - **the region**, where the class map sits in the frame.
 *
 * The display transform: a class palette follows the colour limits (it
 * is sampled at the same stretched position), but not the palette choice
 * or the inversion, which belong to the default palette (AYNI 2D: "Snow
 * keeps its published ramp").
 */

import { DEFAULT_DISPLAY, releaseRampPosition, type ColorScaleDisplay } from './colorScaleDisplay'
import { paletteAt } from './dashRelease'
import type { ReleaseAuxiliary, ReleaseEncoding } from '../types/release-encoding'

/** Table width: one entry per 8-bit code. */
const SIZE = 256

/**
 * The auxiliary map that colours the values: the first with a class
 * palette. Later maps (or one with no palettes) only name classes.
 */
export function colourAuxiliary(enc: ReleaseEncoding): ReleaseAuxiliary | null {
  return enc.auxiliary?.find((a) => a.classes.some((c) => c.stops)) ?? null
}

/**
 * Which class an auxiliary code stands for: the nearest class code at or
 * above the no-data threshold (a lossy codec moves a code a step or two;
 * ties go to the earlier class), else the default class; -1 for none.
 */
export function classifyAuxCode(aux: ReleaseAuxiliary, code: number, nodataThresholdCode: number): number {
  if (!(code >= nodataThresholdCode)) return aux.defaultClass ?? -1
  let best = -1
  let bestDelta = Infinity
  aux.classes.forEach((c, i) => {
    const d = Math.abs(c.code - code)
    if (d < bestDelta) {
      best = i
      bestDelta = d
    }
  })
  return best
}

/** Where a region sits in the frame, as offset + scale (image space, row 0 at the top), like `releaseCropRect`. */
export function auxCropRect(enc: ReleaseEncoding, aux: ReleaseAuxiliary): { u0: number; v0: number; us: number; vs: number } {
  const { x, y, width, height } = aux.region
  return { u0: x / enc.frameWidth, v0: y / enc.frameHeight, us: width / enc.frameWidth, vs: height / enc.frameHeight }
}

/** The same rectangle in THREE's flipped-Y UV space (v == 1 is the top row), like `releaseUvRegion`. */
export function auxUvRegion(enc: ReleaseEncoding, aux: ReleaseAuxiliary): { u0: number; v0: number; us: number; vs: number } {
  const { x, y, width, height } = aux.region
  return { u0: x / enc.frameWidth, v0: 1 - (y + height) / enc.frameHeight, us: width / enc.frameWidth, vs: height / enc.frameHeight }
}

export interface PhaseTables {
  /** RGBA, `SIZE` × `rows`: row 0 the default palette, then one row per class palette. */
  readonly palette: Uint8Array
  readonly rows: number
  /** RGBA, `SIZE` × 1: red is the palette row for an auxiliary code. */
  readonly classMap: Uint8Array
  readonly aux: ReleaseAuxiliary
}


/**
 * The tables for a release whose auxiliary map colours its values, built
 * around the default palette as it is drawn (`baseRow`, `SIZE` × 1 RGBA,
 * indexed by value code). Null when the release has no such map.
 */
export function phaseTables(enc: ReleaseEncoding, baseRow: Uint8Array, display?: ColorScaleDisplay): PhaseTables | null {
  const aux = colourAuxiliary(enc)
  if (!aux || baseRow.length < SIZE * 4) return null
  const paletted = aux.classes.map((c, i) => ({ c, i })).filter(({ c }) => c.stops)
  const rows = 1 + paletted.length
  const palette = new Uint8Array(SIZE * 4 * rows)
  palette.set(baseRow.subarray(0, SIZE * 4), 0)
  const rowOfClass = new Map<number, number>()
  paletted.forEach(({ c, i }, k) => {
    const row = k + 1
    rowOfClass.set(i, row)
    for (let code = 0; code < SIZE; code++) {
      const rgb = paletteAt(c.stops!, releaseRampPosition(enc, display ?? DEFAULT_DISPLAY, code))
      const o = (row * SIZE + code) * 4
      palette[o] = Math.round(rgb[0])
      palette[o + 1] = Math.round(rgb[1])
      palette[o + 2] = Math.round(rgb[2])
      palette[o + 3] = baseRow[code * 4 + 3]!
    }
  })
  const classMap = new Uint8Array(SIZE * 4)
  for (let code = 0; code < SIZE; code++) {
    const cls = classifyAuxCode(aux, code, enc.nodataThresholdCode)
    classMap[code * 4] = rowOfClass.get(cls) ?? 0
    classMap[code * 4 + 1] = cls + 1
    classMap[code * 4 + 3] = 255
  }
  return { palette, rows, classMap, aux }
}

/**
 * The class labels at an auxiliary code, one per auxiliary map (the
 * readout's "· snow"); empty when the release has none.
 */
export function auxLabels(enc: ReleaseEncoding, codes: readonly number[]): string[] {
  const out: string[] = []
  ;(enc.auxiliary ?? []).forEach((aux, i) => {
    const code = codes[i]
    if (code === undefined) return
    const cls = classifyAuxCode(aux, code, enc.nodataThresholdCode)
    const label = cls >= 0 ? aux.classes[cls]?.label : ''
    if (label) out.push(label)
  })
  return out
}
